import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, auth, functions } from '../firebase';
import { collection, onSnapshot, query, doc, getDoc, setDoc, updateDoc, where, getDocs, documentId, orderBy, limit } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { ipfsGateway, resolveIpfsUrl, FIREBASE_PROJECT_ID, API_BASE_URL, BASE_MAX_STAMINA, TOKEN_CONTRACT, TOKENS, tokenContracts, tokenDecimals, buildingConfigClient, ATOMIC_API_ENDPOINTS, itemTemplates, EMBLEM_NAMES, MASTER_ACCOUNT_WAX_ID, ADMIN_ACCOUNTS, STAKING_TIERS, VAULT_TIERS, VAULT_TOKEN_INFO, VAULT_TOKEN_VALUE, getUpgradeCostClient, Client, calculateDiminishedBoost, calculateMaxHp, calculateXpForNextLevel } from '../constants/gameConfig';
import characterImage from '../assets/firecat.png';
import placeholderImage from '../assets/equip.png';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();
const CharacterDisplay = ({ profile, walletAddress, activeEmblem, activeGear, tokenBalances, gangBoosts, vaultUserBoosts, syncPlayerVaultStateCallable, totalMP, baseMP, maxStamina, hasMasterKeycard }) => {
  // >>> ADD THIS STATE AND FUNCTION <<<
  const [isSyncing, setIsSyncing] = React.useState(false);

  const handleSync = async () => {
    if (!syncPlayerVaultStateCallable) return;
    setIsSyncing(true);
    try {
      await syncPlayerVaultStateCallable();
      // The onSnapshot listener in App.js will automatically re-render the UI with the fresh data
    } catch (e) {
      console.error("Sync failed:", e);
    }
    setIsSyncing(false);
  };
  const boostBreakdown = useMemo(() => {
    if (!profile) return {};
    const breakdown = {};

    const add = (type, val, src) => {
      const numVal = parseFloat(val);
      if (!type || isNaN(numVal) || numVal <= 0) return;
      const normalizedType = type === 'NFT Gain' ? 'NFT Gain Chance' : type;
      if (!breakdown[normalizedType]) {
        breakdown[normalizedType] = { raw: 0, sources: [] };
      }
      breakdown[normalizedType].raw += numVal;
      breakdown[normalizedType].sources.push({ name: src, value: numVal });
    };

    add("NFT Gain Chance", 5, "Base System Chance");

    // A. Gear
    if (activeGear && typeof activeGear === 'object') {
      Object.values(activeGear).forEach(item => {
        // 🛡️ THE FIX: Support both Firestore and AtomicAssets data structures
        const templateId = item?.template_id || item?.template?.template_id;
        if (templateId) {
          const t = itemTemplates[templateId];
          if (t && t.boostType) {
            add(t.boostType, t.boostValue, `${t.slot} (${t.rarity})`);
          }
        }
      });
    }

    // B. Emblems
    // 🛡️ THE FIX: Apply the same structural check to Emblems
    const emblemTemplateId = activeEmblem?.template_id || activeEmblem?.template?.template_id;
    if (emblemTemplateId) {
      const tid = String(emblemTemplateId);
      const name = EMBLEM_NAMES[tid] || "Unknown Emblem";
      const typeMap = {
        "872658": "MP Boost", "886383": "Speed Boost", "884183": "MP Boost (Gang/NFTs)",
        "841646": "Points Gain", "871245": "NFT Gain Chance", "872679": "XP Gain",
        "837486": "Stamina Boost", "870362": "NFT Gain Chance"
      };
      const type = typeMap[tid];
      if (type) add(type, 10, `Emblem: ${name}`);
    }

    // C. Vault & Gang & Staking
    if (profile.vault_summary?.totalBoost) add("MP Boost", profile.vault_summary.totalBoost, "Vault Staking");
    if (gangBoosts) {
      Object.entries(gangBoosts).forEach(([k, v]) => {
        if (k === "Base MP Boost") add("MP Boost (Gang/NFTs)", v, "Gang Building");
        else add(k, v, "Gang Building");
      });
    }
    if (profile.nftStakeBoosts) {
      Object.entries(profile.nftStakeBoosts).forEach(([k, v]) => {
        if (k === "Base MP Boost") add("MP Boost (Gang/NFTs)", v, "NFT Staking");
        else add(k, v, "NFT Staking");
      });
    }
    if (profile.partnerMPBoost) add("MP Boost (Gang/NFTs)", profile.partnerMPBoost, "Partner Airdrop");

    // G. DAILY BURN BOOSTS
    if (profile.activeBoosts) {
      const now = Date.now();
      if (profile.activeBoosts.boost_atk > now) add("MP Boost", 10, "Daily Burn Boost");
      if (profile.activeBoosts.boost_def > now) add("Speed Boost", 10, "Daily Burn Boost");
      if (profile.activeBoosts.boost_hp > now) add("MP Boost (Gang/NFTs)", 10, "Daily Burn Boost");
      if (profile.activeBoosts.boost_xp > now) add("XP Gain", 10, "Daily Burn Boost");
      if (profile.activeBoosts.boost_pts > now) add("Points Gain", 10, "Daily Burn Boost");
      if (profile.activeBoosts.boost_luck > now) add("NFT Gain Chance", 10, "Daily Burn Boost");
      if (profile.activeBoosts.boost_stam_max > now) add("Max Stamina", 10, "Daily Burn Boost");
    }

    if (vaultUserBoosts && vaultUserBoosts.burn_boost_expires > Date.now()) {
      add("Vault APR Boost", 0.25, "🔥 Burn Boost");
    }

    return breakdown;
  }, [activeGear, activeEmblem, profile, gangBoosts]);

  if (!profile) return null;

  const getStatBreakdown = () => {
    let rawBonusPercent = 0;
    if (boostBreakdown["MP Boost"]) {
      rawBonusPercent += boostBreakdown["MP Boost"].raw;
    }
    if (boostBreakdown["MP Boost (Gang/NFTs)"]) {
      rawBonusPercent += boostBreakdown["MP Boost (Gang/NFTs)"].raw;
    }

    let flatMP = 0;
    const emblemTid = activeEmblem?.template_id || activeEmblem?.template?.template_id;
    if (emblemTid) {
      const tid = String(emblemTid);
      const burnableEmblems = ["837486", "841646", "870362", "871245", "872658", "872679", "886383"];
      if (burnableEmblems.includes(tid)) {
        flatMP = 50;
      }
    }

    // 🛡️ THE FIX 2: Replace profile.attack with totalMP
    const baseStat = baseMP !== undefined ? baseMP : ((totalMP || profile.totalMP || 0) + flatMP);
    const finalCalculated = totalMP !== undefined ? totalMP : Math.floor(baseStat * (1 + (rawBonusPercent > 0 ? calculateDiminishedBoost(rawBonusPercent) : 0) / 100));

    return {
      base: baseStat,
      levelBonus: (profile.level - 1) * 5,
      rawPercent: rawBonusPercent,
      final: finalCalculated
    };
  };

  const stats = getStatBreakdown();

  // 🛡️ THE FIX 3: Dynamically calculate Max Stamina using your existing boostBreakdown hook
  const stamBoostPct = boostBreakdown["Stamina Boost"] ? calculateDiminishedBoost(boostBreakdown["Stamina Boost"].raw) : 0;
  const flatStam = boostBreakdown["Max Stamina"] ? boostBreakdown["Max Stamina"].raw : 0;
  const calculatedMaxStamina = maxStamina !== undefined ? maxStamina : (Math.floor(100 * (1 + stamBoostPct / 100)) + flatStam);

  const displayName = profile.username || walletAddress || "Unknown Player";

  return (
    <div className="character-display-card" style={{
      background: 'linear-gradient(180deg, rgba(30,30,30,0.9) 0%, rgba(10,10,10,0.95) 100%)',
      padding: '20px', borderRadius: '15px', border: '1px solid #444', marginBottom: '20px', color: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
    }}>
      {/* Top Section (Avatar, Name, Bars) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'center' }}>

        {/* Character Layout */}
        <div style={{ position: 'relative', width: '250px', height: '250px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {/* Main Character Image */}
          <img src={characterImage} alt="Character" style={{ width: '180px', height: '180px', borderRadius: '20px', border: `3px solid ${profile.level >= 10 ? '#ffd700' : '#ff9800'}`, objectFit: 'cover' }} />

          <div style={{ position: 'absolute', bottom: '10px', background: '#333', padding: '4px 15px', borderRadius: '15px', border: '2px solid #ff9800', fontSize: '14px', fontWeight: 'bold', color: '#ff9800', zIndex: 10 }}>Lvl {profile.level}</div>

          {/* Render Equipped Gear Icons Around Character */}
          {activeGear && Object.keys(activeGear).map((slot, index) => {
            if (!activeGear[slot]) return null;

            // 🛡️ THE FIX: Extract Template ID safely
            const templateId = activeGear[slot].template_id || activeGear[slot].template?.template_id;
            const t = itemTemplates[templateId] || {};
            const itemName = activeGear[slot].name || t.name || `Item ${templateId}`;

            // 🛡️ THE FIX: Extract image safely
            const rawImg = activeGear[slot].data?.img || activeGear[slot].immutable_data?.img || t.img;
            const itemImage = resolveIpfsUrl(rawImg);

            // Positioning slots around the character
            const positions = {
              'Sword': { top: '0', left: '10px' },
              'Shield': { top: '0', right: '10px' },
              'Boots': { bottom: '0', left: '10px' },
              'Book': { bottom: '0', right: '10px' },
              'Charm': { top: '50%', left: '-15px', transform: 'translateY(-50%)' },
              'Crest': { top: '50%', right: '-15px', transform: 'translateY(-50%)' },
              'Emblem': { top: '-30px', left: '50%', transform: 'translateX(-50%)' }
            };
            const pos = positions[slot] || {};
            const slotNames = {
              'Crest': 'Points Boost (Crest)',
              'Book': 'XP Boost (Book)',
              'Charm': 'NFT Chance (Charm)',
              'Boots': 'Stamina Boost (Boots)',
              'Shield': 'Speed Boost (Shield)',
              'Sword': 'MP Boost (Sword)',
              'Emblem': 'Emblem'
            };
            const displaySlot = slotNames[slot] || slot;

            return (
              <div key={slot} style={{ position: 'absolute', ...pos, width: '50px', height: '50px', background: '#111', borderRadius: '10px', border: '2px solid #666', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', zIndex: 5, boxShadow: '0 2px 5px rgba(0,0,0,0.8)' }} title={`${displaySlot}: ${itemName}`}>
                {itemImage ? (
                  <img src={itemImage} alt={slot} style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: '10px', color: '#888' }}>No Img</span>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ flex: 1, minWidth: '300px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #555', paddingBottom: '5px', marginBottom: '10px' }}>
            <h3 style={{ margin: 0 }}>{displayName}</h3>

            <button onClick={handleSync} disabled={isSyncing} className="modern-button" style={{ margin: 0, padding: '4px 10px', fontSize: '12px', background: isSyncing ? '#555' : 'linear-gradient(145deg, #00e5ff, #008699)', color: '#fff' }}>
              {isSyncing ? 'Syncing...' : '🔄 Sync Gear & Boosts'}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '15px', background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px' }}>
            <div title="Base Mining Power">⛏️ Base MP: <strong style={{ color: '#00e5ff' }}>{Number(stats.base || 0).toLocaleString()}</strong></div>
            <div title="Total Mining Power (After Boosts)">💥 Total MP: <strong style={{ color: '#00e5ff' }}>{Number(stats.final || 0).toLocaleString()}</strong></div>
            {hasMasterKeycard && (
              <div style={{ width: '100%', fontSize: '12px', color: '#00e5ff', marginTop: '4px', fontWeight: 'bold' }}>
                🔐 WHATIF Verified Master Keycard Active: +5,000 Base MP credited directly from your wallet
              </div>
            )}
            <div title="Dungeon Speed Boost">🛡️ Speed Boost: <strong style={{ color: '#ffeb3b' }}>+{boostBreakdown["Speed Boost"] ? calculateDiminishedBoost(boostBreakdown["Speed Boost"].raw) : 0}%</strong></div>
            <div title="Stamina Boost">👢 Stam Boost: <strong style={{ color: '#8bc34a' }}>+{boostBreakdown["Stamina Boost"] ? calculateDiminishedBoost(boostBreakdown["Stamina Boost"].raw) : 0}%</strong></div>
            <div title="Points Gain">🏅 Points: <strong style={{ color: '#ff9800' }}>+{boostBreakdown["Points Gain"] ? calculateDiminishedBoost(boostBreakdown["Points Gain"].raw) : 0}%</strong></div>
            <div title="XP Gain">📜 XP: <strong style={{ color: '#9c27b0' }}>+{boostBreakdown["XP Gain"] ? calculateDiminishedBoost(boostBreakdown["XP Gain"].raw) : 0}%</strong></div>
            <div title="NFT Gain Chance">🍀 NFT Gain: <strong style={{ color: '#4caf50' }}>+{boostBreakdown["NFT Gain Chance"] ? calculateDiminishedBoost(boostBreakdown["NFT Gain Chance"].raw) : 0}%</strong></div>
            <div title="Resets Daily at 8:00 AM EST">⚡ STAM: <strong style={{ color: profile.stamina <= 0 ? '#f44336' : '#fff' }}>{profile.stamina}</strong> <span style={{ color: '#888' }}>/ {calculatedMaxStamina}</span></div>
            {!isNative && <div title="Vault APR Boost">🏦 Vault APR: <strong style={{ color: '#00e5ff' }}>+{boostBreakdown["Vault APR Boost"] ? boostBreakdown["Vault APR Boost"].raw : 0}%</strong></div>}
            <div title="Max Stamina Bonus">🔥 Max Stamina: <strong style={{ color: '#f44336' }}>+{boostBreakdown["Max Stamina"] ? boostBreakdown["Max Stamina"].raw : 0}</strong></div>
          </div>
        </div>
      </div>

      {/* --- 5. ACTIVE BOOST BREAKDOWN UI --- */}
      <div style={{ border: '1px solid #ff9800', padding: '15px', borderRadius: '8px', marginTop: '20px', background: 'rgba(0,0,0,0.2)' }}>
        <h4 style={{ color: '#ff9800', marginTop: 0 }}>🔥 Active Boosts Analysis</h4>
        {Object.keys(boostBreakdown).length === 0 ? (
          <div style={{ color: '#aaa', fontStyle: 'italic' }}>No active boosts found. Equip Gear, Join a Gang, or Burn Tokens to get stronger!</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>

            {Object.entries(boostBreakdown).map(([type, data]) => {
              // --- FIXED DISPLAY LOGIC ---
              const displayNames = {
                'MP Boost': 'MP Boost',
                'Speed Boost': 'Speed Boost',
                'Stamina Boost': 'Stamina Boost',
                'Points Gain': 'Points Boost',
                'XP Gain': 'XP Boost',
                'NFT Gain Chance': 'NFT Gain Chance',
                'MP Boost (Gang/NFTs)': 'MP (Gangs/NFTs)',
                'Max Stamina': 'Max Stamina',
                'Vault APR Boost': 'Vault APR Boost'
              };
              const displayName = displayNames[type] || type;

              // 🛡️ THE FIX: Accurately separate flat values from % values that bypass diminishing math
              const isFlat = type === "Max Stamina";
              const isExempt = type === "Max Stamina" || type === "Vault APR Boost";

              const diminished = isExempt ? data.raw : calculateDiminishedBoost(data.raw);

              return (
                <div key={type} style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px' }}>
                    <span style={{ color: '#00e5ff', fontSize: '13px' }}>{displayName}</span>
                    <span style={{ color: '#4caf50', fontSize: '13px' }}>+{diminished}{isFlat ? '' : '%'}</span>
                  </div>
                  {!isExempt &&
                    <div style={{ fontSize: '10px', color: '#888', marginBottom: '8px', textAlign: 'right' }}>(Raw Total: {data.raw.toFixed(2)}%)</div>
                  }
                  <ul style={{ margin: '0', paddingLeft: '15px', fontSize: '11px', color: '#ddd' }}>
                    {data.sources.map((src, i) => (
                      <li key={i}>{src.name}: +{src.value}{isFlat ? '' : '%'}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ fontSize: '10px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
          *Diminishing returns apply above 200% total boost per stat (excluding flat bonuses).
        </div>
      </div>
    </div>
  );
};

export default CharacterDisplay;
